import { templatePages, type TemplatePage } from './templates';
export { FUNNEL_OFFER_ON } from './funnel-offer';

/*
 * The ad landing pages (/free/<template-slug>): one per template that has the Google Sheet (`leadSheet`), reusing its data, PDF and email form under a
 * stripped layout. They are noindex, are never in routes.json or the sitemap, and are prerendered from their own list (funnel-routes.json).
 */

/** Build-time switch: unset = no funnel page and no route that renders. The Google Sheet form keeps its own switches (template-lead.ts). */
export const FUNNEL_PAGES_ON = import.meta.env.VITE_FUNNEL_PAGES === 'true';

/** The templates that have a funnel page. */
export const funnelPages = (): TemplatePage[] => templatePages.filter((p) => p.leadSheet);

/**
 * The picture of a template's real Google Sheet (public/images/funnel/<slug>.jpg). The `v` part is part of the URL on purpose: Cloudflare caches
 * image-looking URLs by extension, and a picture asked for before its deploy finished is cached as the app shell for hours. Change `v` when a picture changes.
 */
const SHEET_IMAGE_VERSION = '1';
export const sheetImage = (slug: string): string => `/images/funnel/${slug}.jpg?v=${SHEET_IMAGE_VERSION}`;

/** "Painting" from `painting-estimate-template`, "Pressure Washing" from `pressure-washing-quote-template`. */
export const tradeName = (slug: string): string =>
  slug
    .replace(/-(estimate|quote)-template$/, '')
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** `/free/<slug>` for each of them: the same list as src/marketing/funnel-routes.json (a test keeps the two equal). */
export const funnelPaths = (): string[] => funnelPages().map((p) => `/free/${p.slug}`);

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;
const UTM_MAX = 100;

/**
 * The ad source from a query string: only the four utm keys, control characters dropped, trimmed, at most 100 characters, and only the ones that are
 * left non-empty. Anything else in the query (gclid, fbclid, junk) is not read. The values are sent as JSON and never written into the page.
 */
export function readUtm(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    // eslint-disable-next-line no-control-regex
    const cleaned = (params.get(key) ?? '').replace(/[\u0000-\u001f\u007f-\u009f]/g, '').trim();
    // by code point, not UTF-16 unit: cutting an emoji in half leaves a lone surrogate that JSON.stringify escapes and a Rust JSON parser refuses
    const value = Array.from(cleaned).slice(0, UTM_MAX).join('').trim();
    if (value) out[key] = value;
  }
  return out;
}
